import type { AccessLevel, GrantRole, PlanListingResponse, PublicMode } from '../api/types';

/**
 * The English catalogue (ADR-031) — every user-facing string of the SPA, visible text and
 * accessible names alike, grouped by the screen or component that renders it. English is the
 * default and the fallback, and this object is also the SHAPE of every catalogue: `Messages`
 * is `typeof en`, so `nb` and `nn` must carry exactly these keys with these arities. Hence no
 * `as const` — a leaf is a `string` or a `(x: string) => string`, never a literal type.
 * Parameterized strings are functions; components never concatenate fragments.
 *
 * Deliberately NOT here (ADR-031): the brand "Planlets" is a key but never translated; the
 * install identity (`android 1.2 (34)`) and the version badge's number ride through as
 * parameters; the dev-only `<Diagnostics>` probe (ADR-029 §7 Maestro assertions) and the
 * server's `Code: description` error texts (ADR-002) stay as they are. Pure and DOM-free —
 * `api/errors.ts` in the headless core reads `en.errors` as its default, and `auth/session.ts`
 * gives its `AuthFlowError`s their English message from `en.auth`.
 */
export const en = {
  resources: {
    snapshotReferencesHelp: "A snapshot requires every included reference to be publicly available.",
    willPublish: "Made public",
    keptPrivate: "Kept private",
    loading: "Loading resources…",
    catalogue: "My catalogue",
    intro: "Presentations, tasks and data, connected in one place.",
    shared: "Shared resources",
    empty: "No resources here yet.",
    contents: "Contents",
    name: "Resource name",
    kind: "Resource type",
    create: "Add resource",
    example: "Try a company example",
    exampleHelp: "Creates a private presentation with fictional quarterly figures, assumptions and calculations.",
    address: "Resource URN",
    open: "Open resource",
    copy: "Copy address",
    copied: "Address copied",
    copyFailed: "Select and copy the address above.",
    back: "Back to catalogue",
    parent: "Open parent",
    edit: "Edit content",
    save: "Save content",
    cancel: "Cancel",
    body: "Text",
    completed: "Completed",
    events: "Calendar events",
    eventsHelp: "One event per line: YYYY-MM-DD, title.",
    unit: "Unit",
    frequency: "Period frequency",
    data: "Time series CSV",
    dataHelp: "Two columns: period,value. Use 2026-Q1, 2026-01, YYYY-MM-DD or value for a scalar. Leave a value empty for missing data.",
    importCsv: "Import CSV file",
    derived: "Calculate from inputs",
    expression: "Formula",
    inputs: "Named inputs",
    inputsHelp: "One input per line: revenue = urn:uuid:…",
    formulaHelp: "Use +, −, *, /, parentheses, sum(series), forecast(history, growth, quarters), or align(series, template). Calculations run on the server.",
    reference: "Target resource URN",
    follow: "Open referenced resource",
    live: "Live",
    snapshot: "Snapshot",
    calculated: "Last calculated",
    captured: "Captured",
    notCalculated: "Not calculated yet",
    unavailable: "This content is unavailable. Refresh to try again.",
    period: "Period",
    value: "Value",
    missing: "Missing",
    refresh: "Refresh",
    publish: "Publish",
    publishLive: "Publish live",
    publishSnapshot: "Publish snapshot",
    liveHelp: "The public address stays the same. Open or refresh the resource to see the latest input values.",
    snapshotHelp: "Create a separate public resource containing fixed content and calculated values. It has its own address.",
    publicationBlocked: "Cannot publish: a calculation cannot be evaluated or uses another person’s private data. Publishing results from other people’s private inputs is not allowed yet.",
    previewHelp: "Contained resources follow their visibility settings. References do not grant access to their targets.",
    unavailableReferences: "References public viewers cannot open",
    unpublish: "Unpublish",
    permissions: "Permissions",
    inheritance: "Children inherit access unless overridden. References keep their own permissions.",
    move: "Move resource",
    destination: "Destination parent URN",
    delete: "Delete resource",
    deleteHelp: "Contained resources are deleted too. You can undo this from your catalogue.",
    undo: "Undo deletion",
    deleted: "Resource deleted",
    badCsv: "Use exactly two CSV columns and finite numbers. Periods must be unique.",
    badInputs: "Use a unique input name = urn:uuid:… on each line.",
    badEvents: "Use a valid date and title on each calendar row.",
    fileFailed: "The file could not be read. Paste its contents into the CSV field.",
    snapshotImmutable: "This snapshot has fixed content. Its visibility and permissions can still be changed.",
    rename: "Rename resource",
    unknownType: "This resource type has no web editor yet.",
    types: {
      "catalogue": "Catalogue",
      "presentation": "Presentation",
      "list": "List",
      "task": "Task",
      "calendar": "Calendar",
      "time-series": "Time series",
      "reference": "Reference",
      "note": "Note",
    } as Record<string, string>,
    frequencies: {
      quarterly: "Quarterly",
      monthly: "Monthly",
      daily: "Daily",
      scalar: "Single value",
    },
  },
  /** The composition shell — `App.tsx`. */
  app: {
    /** The page heading on every screen. The brand: identical in every locale. */
    title: 'Planlets',
    tagline: 'Composable planning components — build plans from shareable blocks.',
    /** The status line while the token carries no email claim. */
    signedInWithoutEmail: 'Signed in',
    signedIn: (email: string) => `Signed in as ${email}`,
    signOut: 'Sign out',
    /** The button closing the alert banner (auth errors and failed requests alike). */
    dismiss: 'Dismiss',
    /** The sign-in notice after a 401 with no plan open (launch decision 12). */
    sessionExpired: 'Your session expired — sign in again.',
    /** The same notice while a plan was open — the next sign-in resumes it. */
    sessionExpiredWith: (name: string) =>
      `Your session expired — sign in again to continue with “${name}”.`,
    /** The banner when the round-trip fails without a message of its own. */
    signInFailed: 'Sign-in failed. Try again.',
    /** The delete confirmation's heading, message and confirm button (ADR-029 §1). */
    deleteTitle: (name: string) => `Delete “${name}”?`,
    deleteMessage: 'You can undo this from the home screen.',
    delete: 'Delete',
    /** The name "Make it mine" gives the owned copy (ADR-027 §7 import). */
    importSuffix: (name: string) => `${name} (mine)`,
    /** The title of a block added inside another block from the tree. */
    newTaskTitle: 'New task',
  },

  /** The signed-out screen — `SignIn.tsx`. */
  signIn: {
    heading: 'Sign in',
    lead: 'Planlets uses your platform account — pick how you want to sign in.',
    /** One button per identity provider; the provider's display name comes from the auth server. */
    withProvider: (provider: string) => `Sign in with ${provider}`,
    /** Accessible name of the shell-identity footer (ADR-029 §7). */
    installLabel: 'install',
    /** The footer's text; the install identity (`android 1.2 (34)`) is never translated. */
    installLine: (install: string) => `Planlets ${install}`,
  },

  /** The signed-in landing — `HomeScreen.tsx` (ADR-027 §5). */
  home: {
    /** The status line after a soft delete, next to the Undo button. */
    deleted: (name: string) => `Deleted “${name}”.`,
    undo: 'Undo',
    heading: 'My plans',
    loading: 'Loading your plans…',
    empty: 'No plans yet — create your first one below.',
    /** Accessible name of a row's role badge. */
    roleFor: (name: string) => `role for ${name}`,
    refresh: 'Refresh',
  },

  /** The create / open-by-id forms under the listing — `OpenPlanForm.tsx`. */
  openPlan: {
    createHeading: 'Create a plan',
    nameLabel: 'Plan name',
    namePlaceholder: 'e.g. Summer trip',
    create: 'Create',
    openHeading: 'Open a plan',
    idLabel: 'Plan id',
    idPlaceholder: 'paste a plan id',
    open: 'Open',
  },

  /** The open plan's title bar — `PlanHeader.tsx`. */
  header: {
    /** Accessible name of the version badge; the badge itself shows `v12`, never translated. */
    planVersion: 'plan version',
    /** Accessible name of the access badge (the `access` texts). */
    yourAccess: 'your access',
    rename: 'Rename',
    share: 'Share',
    move: 'Move',
    delete: 'Delete',
    makeItMine: 'Make it mine',
    close: 'Close',
    /** Accessible name of the rename input. */
    newPlanName: 'new plan name',
    save: 'Save',
    cancel: 'Cancel',
  },

  /** The access badge text per `X-Access-Level` value (ADR-027 §7) — header and sub-planlets. */
  access: {
    owner: 'Owner',
    write: 'Can edit',
    read: 'Read-only',
    publicRead: 'Read-only (public)',
    none: 'No access',
  } as Record<AccessLevel, string>,

  /** The role badge per home-listing row (ADR-027 §5). */
  role: {
    owner: 'Owner',
    writer: 'Can edit',
    reader: 'Read-only',
    none: 'No access',
  } as Record<PlanListingResponse['role'], string>,

  /** The add-block form and the empty states — `PlanEditor.tsx`. */
  editor: {
    /** Accessible names of the type select and the title input. */
    blockType: 'block type',
    blockTitle: 'block title',
    titlePlaceholder: 'What are you planning?',
    addBlock: 'Add block',
    /** The empty state at ≥ Write, and below it. */
    emptyEditable: 'No blocks yet — add the first one above.',
    emptyReadOnly: 'No blocks yet.',
  },

  /**
   * The block types as shown — in the type select and as the `(type)` tag next to every block's
   * title. English shows the raw wire ids, so `(note)` still renders `(note)`; a type not listed
   * here falls back to its raw id in every locale.
   */
  blockType: {
    'todo-list': 'todo-list',
    task: 'task',
    note: 'note',
  } as Record<string, string>,

  /** The sub-planlets under the open plan — `ChildPlans.tsx`. */
  childPlans: {
    heading: 'Sub-planlets',
    loading: 'Loading sub-planlets…',
    empty: 'No sub-planlets.',
    /** Accessible name of a child's access badge (the `access` texts). */
    accessTo: (name: string) => `access to ${name}`,
    /** Accessible name of the new sub-planlet's name input. */
    nameLabel: 'sub-planlet name',
    namePlaceholder: 'e.g. Packing list',
    add: 'Add sub-planlet',
  },

  /**
   * One generic block in the tree — `ComponentNode.tsx`. The visible button texts are shared
   * with the to-do list's list-level and task-level rows (`blocks/TodoList.tsx`); the
   * accessible names carry the block's title so no two buttons share a name.
   */
  node: {
    up: '↑',
    down: '↓',
    renameButton: 'Rename',
    addInsideButton: '+ Add inside',
    removeButton: 'Remove',
    save: 'Save',
    cancel: 'Cancel',
    moveUp: (title: string) => `move ${title} up`,
    moveDown: (title: string) => `move ${title} down`,
    rename: (title: string) => `rename ${title}`,
    addInside: (title: string) => `add inside ${title}`,
    remove: (title: string) => `remove ${title}`,
    /** Accessible name of the inline rename input. */
    newTitleFor: (title: string) => `new title for ${title}`,
  },

  /** The in-DOM confirmation — `ConfirmDialog.tsx` (title, message and confirm text come from the caller). */
  confirm: {
    cancel: 'Cancel',
  },

  /** The owner's sharing panel — `ShareDialog.tsx` (ADR-027 §3/§4/§6). */
  share: {
    /** The heading while the owner's view loads, and once the plan's name is known. */
    heading: 'Share',
    headingFor: (name: string) => `Share “${name}”`,
    done: 'Done',
    loading: 'Loading sharing settings…',
    visibility: 'Visibility',
    /** Accessible name of the visibility select. */
    publicMode: 'public mode',
    /** The visibility options per wire public mode. */
    mode: {
      inherit: 'Inherit from parent',
      private: 'Private',
      public: 'Public — anyone with the link',
    } as Record<PublicMode, string>,
    /** The visible label of the link field, and its accessible name. */
    linkLabel: 'Share link',
    shareLink: 'share link',
    rotateLink: 'Rotate link',
    people: 'People',
    nobodyElse: 'Nobody else has access.',
    /** Accessible name of a grantee's role select. */
    roleFor: (email: string) => `role for ${email}`,
    /** The role options per wire grant role. */
    grantRoles: {
      reader: 'Can read',
      writer: 'Can edit',
      none: 'Blocked',
    } as Record<GrantRole, string>,
    /** Accessible name of a grantee's remove button, and its visible text. */
    remove: (email: string) => `remove ${email}`,
    removeButton: 'Remove',
    /** The add-grant form: accessible names of its email input and role select. */
    grantEmail: 'grant email',
    emailPlaceholder: 'name@example.com',
    grantRole: 'grant role',
    add: 'Add',
  },

  /** The owner's move panel — `MoveDialog.tsx` (ADR-027 §3/§4). */
  move: {
    heading: (name: string) => `Move “${name}”`,
    loading: 'Loading destinations…',
    /** Accessible name of the current-location line, and its two texts. */
    currentLocation: 'current location',
    atTopLevel: 'At the top level.',
    under: (name: string) => `Under “${name}”.`,
    noOtherPlans: 'You own no other plans to move this one into.',
    /** The destination select's visible label, accessible name and first option. */
    moveTo: 'Move to',
    destination: 'destination',
    topLevel: 'Top level (no parent)',
    move: 'Move',
    cancel: 'Cancel',
  },

  /** The anonymous read-only view behind a share link — `SharedViewer.tsx` (ADR-027 §6). */
  sharedViewer: {
    /** After the "Planlets" home link and a " · " separator. */
    sharedView: 'shared view',
    backToRoot: 'Back to the shared plan',
    loading: 'Loading…',
    /** Accessible name of the version badge (`v12`). */
    planVersion: 'plan version',
    empty: 'Nothing here yet.',
    subPlanlets: 'Sub-planlets',
    noPublicSubPlanlets: 'No public sub-planlets.',
  },

  /**
   * The to-do block — `blocks/TodoList.tsx` (ADR-029 §3). Exactly the keys of the `TODO`
   * object every test layer selects by (`blocks/todoLabels.ts` re-exports this group), plus the
   * input's placeholder. List-level names carry the word "list" so a task titled like its list
   * never shares a name. The visible Rename / Remove / arrow buttons use the `node` texts.
   */
  todo: {
    addTask: 'Add task',
    /** Accessible name of the new-task input. */
    newTask: 'new task',
    addPlaceholder: 'Add a task',
    empty: 'No tasks yet.',
    save: 'Save',
    cancel: 'Cancel',
    /** Accessible name of the whole checklist region. */
    list: (title: string) => `to-do list ${title}`,
    listMoveUp: (title: string) => `move list ${title} up`,
    listMoveDown: (title: string) => `move list ${title} down`,
    listRename: (title: string) => `rename list ${title}`,
    listNewTitle: (title: string) => `new title for list ${title}`,
    listRemove: (title: string) => `remove list ${title}`,
    /** Accessible name of a task's checkbox. */
    done: (title: string) => `${title} done`,
    rename: (title: string) => `rename ${title}`,
    newTitle: (title: string) => `new title for ${title}`,
    moveUp: (title: string) => `move ${title} up`,
    moveDown: (title: string) => `move ${title} down`,
    remove: (title: string) => `remove ${title}`,
  },

  /** The client-worded banner texts — `api/errors.ts` (everything else is the server's text). */
  errors: {
    unreachable: 'The service is unreachable. Try again.',
    /** The depth cap (ADR-027 §3); the description is the server's, appended as it came. */
    maxNesting: (description: string) => `Maximum nesting reached — ${description}`,
  },

  /**
   * The sign-in round-trip's own failures — `auth/session.ts` (`AuthFlowError`, worded by the
   * banner in `App.tsx`); the status is the auth server's HTTP answer, as a string.
   */
  auth: {
    parFailed: (status: string) => `Sign-in could not start (PAR ${status}).`,
    noneInProgress: 'No sign-in is in progress; start again.',
    stateMismatch: 'The sign-in response does not match this browser session.',
    tokenFailed: (status: string) => `Sign-in could not complete (token ${status}).`,
  },

  /**
   * Why a WebView cannot run the app (ADR-029 §2), per `UnsupportedReason` of the platform
   * port — the only thing such a WebView is shown.
   */
  unsupported: {
    'insecure-context': 'This WebView does not run the app in a secure context.',
    'no-webcrypto': 'This WebView has no WebCrypto — update Android System WebView.',
    'no-random-uuid':
      'This WebView is too old (no crypto.randomUUID) — update Android System WebView.',
  } as Record<'insecure-context' | 'no-webcrypto' | 'no-random-uuid', string>,

  /** The language picker — `i18n/LanguagePicker.tsx`; the options are the autonyms. */
  language: {
    label: 'Language',
  },

  /** The Grove navigation, appearance settings, and supporting interface copy. */
  grove: {
    headerNote: 'Space to move things forward.',
    appearance: 'Appearance',
    system: 'System',
    light: 'Light',
    dark: 'Dark',
    workspace: 'Workspace',
    plans: 'Plans',
    shared: 'Shared',
    settings: 'Settings',
    sidebarNote: 'One space. Many possibilities.',
    skipContent: 'Skip to content',
    settingsEyebrow: 'Make yourself at home',
    settingsDescription: 'A few choices to make this space yours.',
    appearanceDescription: 'Choose a look, or follow your device. Your preference stays on this device.',
    colorMode: 'Color mode',
    account: 'Your account',
    accountDescription: 'Use the same account to pick up your plans on web, iOS, and Android.',
    planActions: 'Plan actions',
    actionsFor: (title: string) => `Actions for ${title}`,
    addTaskTo: (title: string) => `Add task to ${title}`,
    subplanDescription: 'Keep related plans together.',
    subplanPlaceholder: 'Give your related plan a name',
    progressTitle: 'Plan progress',
    progressSummary: (completed: number, total: number) => `${completed} of ${total} tasks complete`,
    completedTasks: 'Completed tasks',
    remainingTasks: (remaining: number) => `Remaining: ${remaining}`,
    listComplete: (completed: number, total: number) => `${completed} / ${total} complete`,
    homeEyebrow: 'Your space',
    homeDescription: 'A clear place for your projects and everyday plans.',
    sharedEyebrow: 'Working together',
    sharedHeading: 'Shared with me',
    sharedDescription: 'The plans people have invited you to read or work on.',
    sharedLoading: 'Loading shared plans…',
    sharedEmpty: 'No shared plans yet. Plans shared with your account will appear here.',
    createDescription: 'Give your next project or personal plan a place to start.',
    openDescription: 'Already have a plan id? Pick up where you left off.',
    namePlaceholder: 'e.g. Product launch or summer trip',
    signinEyebrow: 'A little structure. More possibility.',
    signinHeadingStart: 'Make room for',
    signinHeadingEnd: 'what comes next.',
    signinDescription: 'Bring projects, shared tasks, and personal plans together in one clear space.',
    planPrinciple: 'Start with a plan.',
    planPrincipleDescription: 'Break it into manageable pieces.',
    togetherPrinciple: 'Build it together.',
    togetherPrincipleDescription: 'Give the right people access.',
    movePrinciple: 'Keep things moving.',
    movePrincipleDescription: 'One next step at a time.',
    welcome: 'Welcome to Planlets',
    signinAccount: 'Choose an account to continue to your plans.',
    accountNote: 'Use the same account on web, iOS, and Android.',
    collaborate: 'Collaborate',
    sharingDescription: 'Choose how this plan is shared and what each person can do.',
    emailAddress: 'Email address',
    permission: 'Permission',
    organize: 'Organize',
    sharedNavigation: 'Shared plan navigation',
    sharedReadOnly: 'Shared view · Read-only',
    sharedPlan: 'Shared plan',
  },
};
